import Occasion from './Occasion';
import { Set } from 'immutable';
import { ID, IDRecord, SearchSort, SearchSortBy, SearchSortByField } from '../types';
import Contact from '../models/Contact';

const startsWithANumber = (input: string) => /^\d/.test(input);
const lessThanNumber = (a: string, b: string) => a < b;
const lessThanString = (a: string, b: string) => {
  // if the first character of one input is a number and the other is not, the number input is less than the other
  const aStartsWithNumber = startsWithANumber(a);
  const bStartsWithNumber = startsWithANumber(b);

  if (aStartsWithNumber && !bStartsWithNumber) {
    return true;
  }

  if (!aStartsWithNumber && bStartsWithNumber) {
    return false;
  }

  return (a || '').localeCompare(b || '') < 0;
};

const getKeyValue = (contact: Contact, key: SearchSortByField | 'id') => {
  if (key === 'lastName') {
    return contact?.sortLastName;
  }

  if (key === 'firstName') {
    return contact?.sortFirstName;
  }

  if (key === 'id') {
    return contact ? Number(contact.id) : undefined;
  }

  const value = contact?.[key];

  if (value && ['created', 'lastInteractionDate', 'firstInteractionDate'].includes(key)) {
    return value.unix;
  }

  return value || undefined;
};

const sortKeyFallbacks: Partial<Record<SearchSortBy, SearchSortByField[]>> = {
  firstName: ['firstName', 'lastName', 'score'],
  lastName: ['lastName', 'firstName', 'score'],
  created: ['created', 'score'],
  lastInteraction: ['lastInteractionDate', 'score'],
  closeness: ['score'],
};

export const comparatorForSort = (sort: SearchSort, getContact: (id: ID) => Contact) => {
  const dir = sort.direction === 'asc' ? 1 : -1;
  // get the fallback keys for the sort.name
  const sortKeys = sortKeyFallbacks[sort.name] || [];

  const comparator = (a: IDRecord, b: IDRecord, comparatorKeyIdx: number = 0) => {
    const comparatorKey = sortKeys[comparatorKeyIdx] || 'id';
    let _dir = dir;

    let lessThan: (a: number | string | Occasion, b: number | string | Occasion) => boolean =
      lessThanString;

    if (['firstInteraction', 'lastInteractionDate', 'created'].includes(comparatorKey)) {
      lessThan = lessThanNumber;
    } else if (['score', 'id'].includes(comparatorKey)) {
      lessThan = lessThanNumber;
    }

    if (comparatorKeyIdx > 0) {
      if (comparatorKey === 'score') {
        _dir = -1;
      } else if (comparatorKey === 'id') {
        _dir = 1;
      }
    }

    const aValue = getKeyValue(getContact(String(a.id)), comparatorKey);
    const bValue = getKeyValue(getContact(String(b.id)), comparatorKey);
    const noAValue = aValue === undefined || aValue === null;
    const noBValue = bValue === undefined || bValue === null;

    if (aValue === bValue || (noAValue && noBValue)) {
      if (comparatorKey === 'id') {
        return 0;
      }

      return comparator(a, b, comparatorKeyIdx + 1);
    }

    if (noAValue && bValue) {
      return 1;
    }

    if (noBValue && aValue) {
      return -1;
    }

    const isLessThan = lessThan(aValue, bValue);

    if (isLessThan) {
      return -_dir;
    }

    if (!isLessThan) {
      return _dir;
    }

    return 0;
  };

  return comparator;
};

export const mapAndSortContacts = (
  contactIDs: Set<ID>,
  getContact: (id: ID) => Contact,
  sort: SearchSort,
): Contact[] => {
  const comparator = comparatorForSort(sort, getContact);
  return contactIDs
    .toArray()
    .map((id) => ({ id }))
    .sort(comparator)
    .map(({ id }) => getContact(id));
};
